/**
 * PersonasPage — Sheet Migration Wave 3b
 *
 * Personas CRUD list page. Converted from EntityListPage delegation to direct
 * state management in order to support URL routing via useFormSheetUrlRouting
 * (AC-W3b-03). URL routing is opt-in on this page only — PersonaDetailPage and
 * MarketTree do not use it.
 *
 * URL key: "edit-persona" — e.g. /personas?edit-persona=<uuid>
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormSheetUrlRouting } from '@/hooks/useFormSheetUrlRouting';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { showToast } from '@/components/ui/Toast';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, ChevronRight } from 'lucide-react';
import { t } from '@/lib/i18n';
import { __resolve } from '@/lib/i18n';
import {
  useRorosPersonas,
  useRorosPersonaCreate,
  useRorosPersonaUpdate,
  useRorosPersonaRemove,
  useRorosPersonaReorder,
} from '@/hooks/roros/overarching';
import { PersonaFormDialog } from './PersonaFormDialog';
import type { RorosPersonaDTO, CreatePersonaPayload } from '@/types/roros/overarching';

// ─── Labels object — all 9 required label keys as t() literals ───────────────
// These strings are checked by entity-list-page-labels.test.ts; keep them here
// even though PersonasPage now manages state directly (no longer via EntityListPage).

const PERSONAS_URL_KEY = 'edit-persona';

// ─── Component ────────────────────────────────────────────────────────────────

export function PersonasPage() {
  const navigate = useNavigate();

  // Required label strings — checked by unit test entity-list-page-labels.test.ts
  const labels = {
    pageTitle: t('Personas'),
    addButton: t('Add Persona'),
    emptyMessage: t('No personas yet'),
    toastCreated: t('Persona created'),
    toastUpdated: t('Persona updated'),
    toastDeleted: t('Persona deleted'),
    formCreateTitle: t('Add Persona'),
    formEditTitle: t('Edit Persona'),
    deleteConfirmTitle: t('Delete Persona'),
  };

  // ─── State ───────────────────────────────────────────────────────────────
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPersona, setEditingPersona] = useState<RorosPersonaDTO | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  // ─── Data ─────────────────────────────────────────────────────────────────
  const personasQuery = useRorosPersonas();
  const personas: RorosPersonaDTO[] = (personasQuery.data ?? []) as RorosPersonaDTO[];

  // ─── URL routing (AC-W3b-03) ─────────────────────────────────────────────
  // getId must be stable — defined outside the component or memoized.
  const getPersonaId = useCallback((p: RorosPersonaDTO) => p.id, []);

  const { deepLinkTarget, consumeDeepLink } = useFormSheetUrlRouting({
    urlKey: PERSONAS_URL_KEY,
    items: personas,
    getId: getPersonaId,
  });

  useEffect(() => {
    if (!deepLinkTarget) return;
    setEditingPersona(deepLinkTarget);
    setIsFormOpen(true);
    consumeDeepLink();
  }, [deepLinkTarget, consumeDeepLink]);

  // ─── Mutations ────────────────────────────────────────────────────────────
  const createMutation = useRorosPersonaCreate();
  const updateMutation = useRorosPersonaUpdate();
  const removeMutation = useRorosPersonaRemove();
  const reorderMutation = useRorosPersonaReorder();

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const openCreate = useCallback(() => {
    setEditingPersona(null);
    setIsFormOpen(true);
  }, []);

  const openEdit = useCallback((persona: RorosPersonaDTO) => {
    setEditingPersona(persona);
    setIsFormOpen(true);
  }, []);

  const handleFormSubmit = useCallback(
    async (draft: CreatePersonaPayload) => {
      if (editingPersona) {
        updateMutation.mutate(
          { id: editingPersona.id, payload: draft as unknown as Record<string, unknown> },
          {
            onSuccess: () => {
              showToast.success(labels.toastUpdated);
              setIsFormOpen(false);
            },
            onError: (err: Error) => showToast.error(err.message || t('Failed to update')),
          },
        );
      } else {
        createMutation.mutate(draft as unknown as Record<string, unknown>, {
          onSuccess: () => {
            showToast.success(labels.toastCreated);
            setIsFormOpen(false);
          },
          onError: (err: Error) => showToast.error(err.message || t('Failed to create')),
        });
      }
    },
    [editingPersona, updateMutation, createMutation, labels.toastCreated, labels.toastUpdated],
  );

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    removeMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        showToast.success(labels.toastDeleted);
        setDeleteTarget(null);
      },
      onError: (err: Error) => showToast.error(err.message || t('Failed to delete')),
    });
  }, [deleteTarget, removeMutation, labels.toastDeleted]);

  const handleMove = useCallback(
    (items: RorosPersonaDTO[], index: number, direction: 'up' | 'down') => {
      if (direction === 'up' && index === 0) return;
      if (direction === 'down' && index === items.length - 1) return;
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      reorderMutation.mutate([
        { id: items[index].id, sortOrder: items[swapIndex].sortOrder },
        { id: items[swapIndex].id, sortOrder: items[index].sortOrder },
      ]);
    },
    [reorderMutation],
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <RorosPage
      title={labels.pageTitle}
      breadcrumbs={[
        { label: t('Company Settings'), path: '/roros/company-profile' },
      ]}
      actions={
        <Button onClick={openCreate} leftIcon={<Plus className="h-4 w-4" />}>
          {labels.addButton}
        </Button>
      }
    >
      <QueryPage
        query={personasQuery as any}
        emptyMessage={labels.emptyMessage}
        emptyAction={
          <Button onClick={openCreate} leftIcon={<Plus className="h-4 w-4" />}>
            {labels.addButton}
          </Button>
        }
      >
        {(items) => (
          <div className="space-y-3">
            {(items as RorosPersonaDTO[]).map((persona, index, arr) => (
              <Card key={persona.id} padding="sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground">{persona.name}</h3>
                    {persona.role ? (
                      <p className="text-sm text-muted-foreground mt-1">
                        {t('Role')}: {persona.role}
                      </p>
                    ) : null}
                    {persona.decisionRole ? (
                      <div className="mt-1">
                        <Badge variant="secondary" className="text-[10px]">
                          {t(persona.decisionRole)}
                        </Badge>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {/* View details */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/roros/company-profile/personas/${persona.id}`)}
                      aria-label={t('View persona details')}
                      rightIcon={<ChevronRight className="h-4 w-4" />}
                    >
                      {t('View details')}
                    </Button>
                    {/* Reorder */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleMove(arr, index, 'up')}
                      disabled={index === 0 || reorderMutation.isPending}
                      aria-label={t('Move up')}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleMove(arr, index, 'down')}
                      disabled={index === arr.length - 1 || reorderMutation.isPending}
                      aria-label={t('Move down')}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    {/* Edit — opens FormSheet with URL routing */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(persona)}
                      aria-label={t('Edit')}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {/* Delete */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        setDeleteTarget({ id: persona.id, name: persona.name })
                      }
                      aria-label={t('Delete')}
                    >
                      <Trash2 className="h-4 w-4 text-error-600" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </QueryPage>

      {/* Create / Edit sheet — URL routing active for edit mode only */}
      <PersonaFormDialog
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        mode={editingPersona ? 'edit' : 'create'}
        persona={editingPersona}
        onSubmit={handleFormSubmit}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        urlKey={editingPersona ? PERSONAS_URL_KEY : undefined}
        urlId={editingPersona?.id}
      />

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={labels.deleteConfirmTitle}
        description={
          deleteTarget
            ? __resolve('ui::Are you sure you want to delete "{{name}}"? This action cannot be undone.', {
                name: deleteTarget.name,
              })
            : ''
        }
        variant="destructive"
        confirmText={t('Delete')}
        isLoading={removeMutation.isPending}
      />
    </RorosPage>
  );
}
